import { BottomSheetModal, BottomSheetScrollView } from '@gorhom/bottom-sheet';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { Button, Text } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { useGetGeoArea } from '@/features/api/hooks/participant-geo-area/useGetGeoArea';
import { GeoAreaMap } from '@/features/geo-area/components/map/GeoAreaMap';
import {
  RANGE_SLIDER_HEIGHT,
  RangeSlider
} from '@/features/geo-area/components/RangeSlider';
import { SubscriptionMapContent } from '@/features/geo-area/components/subscription/SubscriptionMapContent';
import { getMapHeight } from '@/features/geo-area/constants/map';
import {
  MEINE_REGION_MAX_KM,
  MEINE_REGION_MIN_KM
} from '@/features/geo-area/constants/radius';
import { useRegionalfunkRadiusStore } from '@/features/geo-area/store/useRegionalfunkRadiusStore';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { SheetBackdrop } from '@/shared/components/SheetBackdrop';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

export type MeineRegionRadiusModalHandle = {
  open: () => void;
};

const MIN_METERS = MEINE_REGION_MIN_KM * 1000;
const MAX_METERS = MEINE_REGION_MAX_KM * 1000;

/** Sheet occupies 90% of the height below the top safe-area inset. */
const SHEET_HEIGHT_RATIO = 0.9;

const clampMeters = (m: number): number =>
  Math.min(Math.max(m, MIN_METERS), MAX_METERS);

const clampKm = (km: number): number =>
  Math.min(Math.max(Math.round(km), MEINE_REGION_MIN_KM), MEINE_REGION_MAX_KM);

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    sheetBackground: {
      backgroundColor: theme.colors.surfaceContainerLow
    },
    handleIndicator: {
      backgroundColor: theme.colors.outline
    },
    scrollContent: {
      paddingHorizontal: 16,
      gap: 20
    },
    title: {
      textAlign: 'center'
    },
    description: {
      color: theme.colors.onSurfaceVariant,
      textAlign: 'center'
    },
    mapWrapper: {
      marginHorizontal: -16,
      paddingHorizontal: 16,
      borderRadius: 12,
      overflow: 'hidden'
    },
    labelRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    value: {
      color: theme.colors.primary
    },
    hint: {
      color: theme.colors.onSurfaceVariant,
      textAlign: 'center'
    }
  });

export const MeineRegionRadiusModal = forwardRef<MeineRegionRadiusModalHandle>(
  function MeineRadiusModal(_, ref) {
    const theme = useAppTheme();
    const insets = useSafeAreaInsets();
    const { height } = useWindowDimensions();
    const styles = useMemo(() => makeStyles(theme), [theme]);
    const { t } = useTranslation(LocalizationNameSpace.GEO_AREA, {
      keyPrefix: 'screens.my-radius'
    });

    const sheetRef = useRef<BottomSheetModal>(null);

    const storedRadiusKm = useRegionalfunkRadiusStore(
      (s) => s.meineRegionRadiusKm
    );
    const setStoredRadiusKm = useRegionalfunkRadiusStore(
      (s) => s.setMeineRegionRadiusKm
    );

    // Transient slider state (meters) — kept smooth for the slider,
    // rounded to km only for display and persistence.
    const [radiusMeters, setRadiusMeters] = useState(
      clampMeters(storedRadiusKm * 1000)
    );
    // Separate initial value so mid-drag updates don't snap the slider thumb.
    // Also used as the RangeSlider's `key` so each open gets a fresh mount
    // whose internal `useSharedValue(initialRange)` picks up the current value
    // directly — avoiding the one-frame-plus-animation lag the sync effect
    // would otherwise cause.
    const [initialMeters, setInitialMeters] = useState(
      clampMeters(storedRadiusKm * 1000)
    );

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          // Sync local state to the current store value *before* opening the
          // sheet so the slider renders at the right position from the first
          // frame. Reading via getState() avoids stale closures over
          // `storedRadiusKm`.
          const startMeters = clampMeters(
            useRegionalfunkRadiusStore.getState().meineRegionRadiusKm * 1000
          );
          setRadiusMeters(startMeters);
          setInitialMeters(startMeters);
          sheetRef.current?.present();
        }
      }),
      []
    );

    const { data: person } = useGetPerson();
    const { data: homeArea } = useGetGeoArea(person?.homeAreaId);

    const displayKm = clampKm(radiusMeters / 1000);

    // Base the map size on the space the sheet actually gets (90% of the height
    // below the top inset) rather than the full window height, clamped to
    // sensible bounds.
    const mapHeight = getMapHeight((height - insets.top) * SHEET_HEIGHT_RATIO);

    const handleSave = useCallback(() => {
      setStoredRadiusKm(displayKm);
      sheetRef.current?.dismiss();
    }, [displayKm, setStoredRadiusKm]);

    const canSave = !!homeArea;

    return (
      <BottomSheetModal
        ref={sheetRef}
        enableDynamicSizing={false}
        snapPoints={['90%']}
        topInset={insets.top}
        // Android + New Architecture: the content pan gesture (used to drag the
        // sheet from its scrollable content) conflicts with the ScrollView's
        // native scroll gesture and blocks scrolling entirely. Disabling it
        // keeps the handle drag/pan-down-to-close working while letting the
        // BottomSheetScrollView scroll normally.
        enableContentPanningGesture={false}
        backgroundStyle={styles.sheetBackground}
        handleIndicatorStyle={styles.handleIndicator}
        backdropComponent={SheetBackdrop}
      >
        <BottomSheetScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: 24 + insets.bottom }
          ]}
        >
          <Text variant="headlineLarge" style={styles.title}>
            {t('title')}
          </Text>

          <Text variant="bodyMedium" style={styles.description}>
            {t('description')}
          </Text>

          {homeArea ? (
            <View
              style={[
                styles.mapWrapper,
                { minHeight: mapHeight + RANGE_SLIDER_HEIGHT }
              ]}
            >
              <View style={{ height: mapHeight }}>
                <GeoAreaMap>
                  <SubscriptionMapContent
                    homeGeoArea={homeArea}
                    visibleGeoAreas={[]}
                    selectedIds={[]}
                    range={radiusMeters}
                  />
                </GeoAreaMap>
              </View>
              <RangeSlider
                key={initialMeters}
                initialRange={initialMeters}
                minRange={MIN_METERS}
                maxRange={MAX_METERS}
                onRangeChange={(m) => setRadiusMeters(clampMeters(m))}
              />
            </View>
          ) : (
            <Text variant="bodySmall" style={styles.hint}>
              {t('preview.noHomeArea')}
            </Text>
          )}

          <View style={styles.labelRow}>
            <Text variant="titleMedium">{t('sliderLabel')}</Text>
            <Text variant="labelLarge" style={styles.value}>
              {t('value', { km: displayKm })}
            </Text>
          </View>

          <Button mode="contained" onPress={handleSave} disabled={!canSave}>
            {t('save')}
          </Button>
        </BottomSheetScrollView>
      </BottomSheetModal>
    );
  }
);

MeineRegionRadiusModal.displayName = 'MeineRegionRadiusModal';
